import axiosInstance from './index';

// axiosInstance.baseURL already ends in /manage, so paths here are relative to it and
// the shared auth-header / jwt-expiry interceptors apply automatically.
//
// NOTE: orgId is deliberately never sent. Every /ontology route takes the organisation
// from the JWT and fails closed without one — a request with no org reaches the system
// and regulatory tiers only, never another tenant's documents. Passing an org from the
// client would be both redundant and a way to ask for someone else's data.

/**
 * The three ontology resources, keyed by the URL segment the Ontology screens use.
 *
 * Mirrors the RESOURCES table in the control plane's ontologyController so a `kind` in a
 * route param maps to exactly one API path and one response key.
 *
 * WIRE NAMES ONLY. What each collection is called on screen lives in KIND_LABEL
 * (Components/Ontology/ontologyFormat.ts) and is deliberately different — 'concepts' is
 * the path, "Risk Library" is the name. This table carried a `label` field too until it
 * became a second, unread display string one edit away from contradicting the first.
 */
export const RESOURCE_BY_KIND = {
  concepts: { path: 'concepts', one: 'concept', many: 'concepts' },
  policies: { path: 'policies', one: 'policy', many: 'policies' },
  parameters: { path: 'parameters', one: 'parameter', many: 'parameters' },
};

export const KINDS = Object.keys(RESOURCE_BY_KIND);

export const isKind = (kind) => Object.prototype.hasOwnProperty.call(RESOURCE_BY_KIND, kind);

// Codes contain dots and colons (role.pep, alert_type:structuring), so every path
// segment is encoded. A code containing a slash cannot be addressed as one segment —
// the control plane documents the same limit — so callers must not link to one.
const enc = encodeURIComponent;

export const codeIsAddressable = (code) => typeof code === 'string' && code !== '' && !code.includes('/');

/**
 * The seven verbs plus customise, generated per resource rather than written out
 * twenty-four times. Every resource has an identical surface by design.
 */
const makeResourceApi = (path) => ({
  list: (params, { signal } = {}) =>
    axiosInstance.get(`/ontology/${path}`, { params, signal }),

  get: (code, { params, signal } = {}) =>
    axiosInstance.get(`/ontology/${path}/${enc(code)}`, { params, signal }),

  create: (document) => axiosInstance.post(`/ontology/${path}`, document),

  update: (code, document) => axiosInstance.put(`/ontology/${path}/${enc(code)}`, document),

  remove: (code) => axiosInstance.delete(`/ontology/${path}/${enc(code)}`),

  // Approving a PARAMETER is not a metadata edit — within one cache TTL it changes what
  // the detection nodes apply. Callers must confirm before calling this.
  approve: (code, body = {}) =>
    axiosInstance.post(`/ontology/${path}/${enc(code)}/approve`, body),

  // The only legitimate way to change an approved document: closes the current version
  // and stages a DRAFT successor, so nothing is in force until that draft is approved.
  supersede: (code, body = {}) =>
    axiosInstance.post(`/ontology/${path}/${enc(code)}/supersede`, body),

  // A read that writes nothing: returns the in-force global document re-scoped as an
  // organisation draft, which the caller POSTs back via create(). Never present an empty
  // form instead — resolution picks one winner per code and does not merge an org's
  // fields over the platform's, so anything dropped here is simply gone.
  customise: (code, { signal } = {}) =>
    axiosInstance.get(`/ontology/${path}/${enc(code)}/customise`, { signal }),
});

export const conceptsApi = makeResourceApi('concepts');
export const policiesApi = makeResourceApi('policies');
export const parametersApi = makeResourceApi('parameters');

const API_BY_KIND = {
  concepts: conceptsApi,
  policies: policiesApi,
  parameters: parametersApi,
};

export const apiForKind = (kind) => API_BY_KIND[kind] || null;

// ─── Cross-resource views ────────────────────────────────────────────────────

/** The ontology behind one activity node: concepts, policies and thresholds in force. */
export const getActivityContext = (activityTemplateCode, { signal } = {}) =>
  axiosInstance.get(`/ontology/activities/${enc(activityTemplateCode)}/context`, { signal });

// ─── Reverse links: which activity templates use an ontology record ──────────
//
// All three return the same `{ families, family_count, template_count }` shape, with
// templates collapsed to clone families. Derived by walking template configurations, so
// they cannot drift from what the templates say.

/** What breaks if this concept changes — which templates cite it, and where. */
export const getConceptUsage = (code, { signal } = {}) =>
  axiosInstance.get(`/ontology/concepts/${enc(code)}/usage`, { signal });

/** Which templates a parameter configures, and by which of the three binding forms. */
export const getParameterUsage = (code, { signal } = {}) =>
  axiosInstance.get(`/ontology/parameters/${enc(code)}/usage`, { signal });

/**
 * Which templates a policy reaches — INDIRECTLY, via the concepts it governs. The
 * response carries `via: 'governs_concepts'`; the UI must say so rather than imply the
 * policy is attached to the activity.
 */
export const getPolicyUsage = (code, { signal } = {}) =>
  axiosInstance.get(`/ontology/policies/${enc(code)}/usage`, { signal });

/** The usage endpoint for a browse `kind`. */
export const usageApiForKind = (kind) => ({
  concepts: getConceptUsage,
  policies: getPolicyUsage,
  parameters: getParameterUsage,
}[kind] || null);

/** Where a template's drawer lives, for the return leg of the round trip. */
export const agentHref = (activityTemplateCode) =>
  `#/operators-marketplace?agent=${enc(activityTemplateCode)}`;

/** Normalise a usage response; the shape is identical across the three. */
export const normalizeUsage = (res) => {
  const body = res?.data ?? res ?? {};
  return {
    families: Array.isArray(body.families) ? body.families : [],
    familyCount: Number(body.family_count) || 0,
    templateCount: Number(body.template_count) || 0,
    // Present on policies only: the link is via governed concepts, not direct.
    via: body.via ?? null,
    governsConceptCount: Number(body.governs_concept_count) || 0,
  };
};

/** The thresholds one policy sets. */
export const getPolicyParameters = (code, { params, signal } = {}) =>
  axiosInstance.get(`/ontology/policies/${enc(code)}/parameters`, { params, signal });

/** What is actually in force, with the candidates each value beat. */
export const resolveParameters = (params, { signal } = {}) =>
  axiosInstance.get('/ontology/resolve', { params, signal });

/**
 * Counts for the ontology landing cards and the Risk Library's category rail.
 *
 * One request rather than a limit=1 list call per category. Server-side these numbers come
 * from the same query builder the listings use, so a rail count and the paginator's total
 * under the same filters agree.
 */
export const getOntologySummary = ({ signal } = {}) =>
  axiosInstance.get('/ontology/summary', { signal });

/**
 * Search all three collections at once.
 *
 * Someone looking for "structuring" should not have to know first whether it is stored as
 * a concept, named in a policy or bound by a parameter. Capped per kind and unpaged by
 * design — this jumps you to a record; the listings are where browsing happens.
 */
/**
 * Typed rather than destructured, so a caller may pass either option alone. Omitting
 * `limit` leaves the server's own per-kind cap in charge.
 *
 * @param {string} q
 * @param {{limit?: number, signal?: AbortSignal}} [options]
 */
export const searchOntology = (q, options = {}) =>
  axiosInstance.get('/ontology/search', {
    params: { q, limit: options.limit },
    signal: options.signal,
  });

/**
 * What is configured for a batch of concepts — one request per page of rows.
 *
 * The reason this is a batch endpoint at all: the Detection Controls column needs the
 * threshold in force for every row, and asking per row was what kept the column off the
 * list. Capped server-side at 200 codes, which is far more than a page.
 *
 * @param {string[]} conceptCodes
 * @param {{signal?: AbortSignal}} [options]
 */
export const getInForceByConcept = (conceptCodes, options = {}) =>
  axiosInstance.get('/ontology/parameters/in-force', {
    params: { concept_codes: (conceptCodes || []).join(',') },
    signal: options.signal,
  });

// ─── Source documents ────────────────────────────────────────────────────────
//
// The registry a citation points at: published guidance, statute, and the institution's
// own policy PDFs. Read-only — documents are seeded upstream, not authored here.

/**
 * Source documents are DELIBERATELY NOT a `kind`, and must not join RESOURCE_BY_KIND.
 *
 * makeResourceApi generates eight verbs including approve, supersede and delete, and
 * OntologyDetailPage renders its lifecycle buttons from apiForKind(kind) plus
 * lifecycleStateOf(document). Registering documents as a kind would therefore grow an
 * Approve button that POSTs to a route the control plane does not serve.
 *
 * Same reasoning that keeps `coverage` out of KINDS — see the note in OntologyPage.tsx.
 */
export const sourceDocumentsApi = {
  list: (params, { signal } = {}) =>
    axiosInstance.get('/ontology/source-documents', { params, signal }),

  get: (code, { signal } = {}) =>
    axiosInstance.get(`/ontology/source-documents/${enc(code)}`, { signal }),

  /**
   * What a change to this document would reach.
   *
   * `include_wide` adds the thresholds that sit under an affected policy without being
   * tied to a term the document names. On real data that is 45 rows against 2, because
   * one broad policy governs 48 concepts — so it is opt-in, and the COUNT comes back
   * either way so a collapsed disclosure can be labelled without paying for the rows.
   */
  impact: (code, { includeWide = false, signal } = {}) =>
    axiosInstance.get(`/ontology/source-documents/${enc(code)}/impact`, {
      params: includeWide ? { include_wide: 'true' } : undefined,
      signal,
    }),
};

/** A policy's citations, rendered the way an investigator cites them. */
export const getPolicyCitations = (code, { signal } = {}) =>
  axiosInstance.get(`/ontology/policies/${enc(code)}/citations`, { signal });

/** The registry list envelope. Mirrors normalizeList, which is keyed by `kind`. */
export const normalizeDocumentList = (res) => {
  const body = res?.data ?? res ?? {};
  const documents = Array.isArray(body.source_documents) ? body.source_documents : [];
  const p = body.pagination || {};
  return {
    documents,
    pagination: {
      page: Number(p.page) || 1,
      limit: Number(p.limit) || 25,
      total: Number.isFinite(Number(p.total)) ? Number(p.total) : documents.length,
      pages: Number.isFinite(Number(p.pages)) ? Number(p.pages) : 1,
    },
  };
};

export const normalizeSourceDocument = (res) => {
  const body = res?.data ?? res ?? {};
  return body.source_document ?? body.data ?? null;
};

/**
 * The impact envelope, every tier defaulted to an array.
 *
 * The panel renders five sections before this resolves and must not break on a partial or
 * older response — a missing tier degrades to "none", never to a crashed section. Same
 * contract normalizeCoverage keeps.
 */
export const normalizeImpact = (res) => {
  const body = res?.data ?? res ?? {};
  const tiers = body.tiers || {};
  const list = (key) => (Array.isArray(tiers[key]) ? tiers[key] : []);
  return {
    sourceDocCode: body.source_doc_code ?? null,
    title: body.title ?? null,
    scopeLevel: body.scope_level ?? null,
    generatedAt: body.generated_at ?? null,
    totals: {
      concepts: 0, policies: 0, parameters: 0, agents: 0, wide_parameters: 0,
      ...(body.totals || {}),
    },
    tiers: {
      citedBy: list('cited_by'),
      governs: list('governs'),
      governingPolicies: list('governing_policies'),
      thresholds: list('thresholds'),
      // Clone families, the same shape UsedByTemplatesSection already renders.
      agents: list('agents'),
      wideParameters: list('wide_parameters'),
    },
    // Whether the wide rows were actually sent, as opposed to merely counted.
    wideIncluded: Boolean(body.wide_included),
    implements: Array.isArray(body.implements) ? body.implements : [],
    implementedBy: Array.isArray(body.implemented_by) ? body.implemented_by : [],
  };
};

/** Gaps in the live configuration, grouped by finding. */
export const getOntologyCoverage = ({ signal } = {}) =>
  axiosInstance.get('/ontology/coverage', { signal });

/**
 * Your vocabulary: the institution's detection scenarios and the typologies each one
 * detects. A draft is saved by whoever proposes it; approving needs someone else — the
 * control plane refuses the proposer with a 403.
 */
export const scenarioVocabularyApi = {
  list: ({ signal } = {}) => axiosInstance.get('/ontology/vocabulary/scenarios', { signal }),
  saveDraft: (code, body) => axiosInstance.put(`/ontology/vocabulary/scenarios/${enc(code)}/draft`, body),
  discardDraft: (code) => axiosInstance.delete(`/ontology/vocabulary/scenarios/${enc(code)}/draft`),
  approve: (code, body = {}) => axiosInstance.post(`/ontology/vocabulary/scenarios/${enc(code)}/approve`, body),
  forTypology: (code, { signal } = {}) =>
    axiosInstance.get(`/ontology/vocabulary/typologies/${enc(code)}/scenarios`, { signal }),
};

/**
 * One record and the mesh around it.
 *
 * @param {{kind: string, code: string, depth?: number}} params
 * @param {{signal?: AbortSignal}} [options]
 */
export const getOntologyGraph = (params, options = {}) =>
  axiosInstance.get('/ontology/graph', { params, signal: options.signal });

/**
 * The in-force map, keyed by concept code.
 *
 * `resolved` is null whenever the server could not name one unambiguous value — several
 * thresholds bind the concept, or one of them declares a scope. Callers MUST branch on it
 * rather than reading `.value` off nothing: an empty cell and "varies by scope" say very
 * different things to someone tuning a control.
 */
export const normalizeInForce = (res) => {
  const body = res?.data ?? res ?? {};
  return (body.concepts && typeof body.concepts === 'object') ? body.concepts : {};
};

/** The coverage report; `findings` keeps zero-count entries, which are the good news. */
export const normalizeCoverage = (res) => {
  const body = res?.data ?? res ?? {};
  return {
    generatedAt: body.generated_at ?? null,
    // Spread over the zeroes rather than substituted for them: a server that predates
    // the agent-side findings sends three totals, and the summary line must not print
    // "undefined agents" against it.
    totals: {
      concepts: 0, policies: 0, parameters: 0, activity_templates: 0, source_documents: 0,
      ...(body.totals || {}),
    },
    findings: Array.isArray(body.findings) ? body.findings : [],
  };
};

/** The graph envelope. `truncated` means the server capped the neighbourhood. */
export const normalizeGraph = (res) => {
  const body = res?.data ?? res ?? {};
  return {
    focus: body.focus ?? null,
    depth: Number(body.depth) || 1,
    truncated: Boolean(body.truncated),
    nodes: Array.isArray(body.nodes) ? body.nodes : [],
    edges: Array.isArray(body.edges) ? body.edges : [],
  };
};

/**
 * Pre-write checks. Errors block a write; warnings NEVER do — a warning is typically an
 * organisation threshold looser than a regulatory baseline, which is a compliance finding
 * to show the analyst, not a write to refuse. Refusing it would leave the institution
 * with no configured value at all.
 */
export const validateDocument = (resource, document, { signal } = {}) =>
  axiosInstance.post('/ontology/validate', { resource, document }, { signal });

// ─── Normalizers ─────────────────────────────────────────────────────────────

const unwrap = (res, ...keys) => {
  const body = res?.data ?? res;
  for (const key of keys) {
    if (body && body[key] !== undefined && body[key] !== null) return body[key];
  }
  return body;
};

/** The control plane's collection envelope: { <many>: [...], pagination: {...} }. */
export const normalizeList = (res, kind) => {
  const many = RESOURCE_BY_KIND[kind]?.many ?? 'documents';
  const body = res?.data ?? res ?? {};
  const documents = Array.isArray(body[many]) ? body[many] : [];
  const p = body.pagination || {};
  return {
    documents,
    pagination: {
      page: Number(p.page) || 1,
      limit: Number(p.limit) || 25,
      total: Number.isFinite(Number(p.total)) ? Number(p.total) : documents.length,
      pages: Number.isFinite(Number(p.pages)) ? Number(p.pages) : 1,
    },
  };
};

export const normalizeDocument = (res, kind) =>
  unwrap(res, RESOURCE_BY_KIND[kind]?.one ?? 'document', 'data') || null;

/**
 * Parameters returned by the activity context:
 * { in_force: [{parameter_code, resolved, supersedes, binds_via}], pending_draft_count }
 *
 * Defensive because the field is newer than the endpoint: an older control plane returns
 * a context with no `parameters` key at all, and that must render as "none", not a crash.
 */
export const normalizeActivityParameters = (parameters) => ({
  inForce: Array.isArray(parameters?.in_force) ? parameters.in_force : [],
  pendingDraftCount: Number(parameters?.pending_draft_count) || 0,
});

/**
 * The summary envelope, every field defaulted.
 *
 * The landing page renders its cards before this resolves and must not break on a partial
 * or older response — a missing count degrades to zero, never to a crashed card.
 */
export const normalizeSummary = (res) => {
  const body = res?.data ?? res ?? {};
  const concepts = body.concepts || {};
  const policies = body.policies || {};
  const parameters = body.parameters || {};
  const sourceDocuments = body.source_documents || {};
  return {
    concepts: {
      total: Number(concepts.total) || 0,
      byType: (concepts.by_type && typeof concepts.by_type === 'object') ? concepts.by_type : {},
    },
    policies: { total: Number(policies.total) || 0 },
    parameters: {
      total: Number(parameters.total) || 0,
      byStatus: parameters.by_status || {},
      orgDrafts: Number(parameters.org_drafts) || 0,
    },
    // Newer than the endpoint: a control plane that predates the registry sends no such
    // key, and the landing card must render "0" rather than crash on it.
    sourceDocuments: {
      total: Number(sourceDocuments.total) || 0,
      byRefType: (sourceDocuments.by_ref_type && typeof sourceDocuments.by_ref_type === 'object')
        ? sourceDocuments.by_ref_type : {},
    },
  };
};

/** The search envelope. Each result carries its own `kind`, so callers never re-derive it. */
export const normalizeSearch = (res) => {
  const body = res?.data ?? res ?? {};
  return {
    query: body.query ?? '',
    results: Array.isArray(body.results) ? body.results : [],
    counts: body.counts || { concepts: 0, policies: 0, parameters: 0 },
  };
};

/** Split /validate output the way callers must treat it. */
export const normalizeValidation = (res) => {
  const body = res?.data ?? res ?? {};
  return {
    valid: body.valid !== false,
    errors: Array.isArray(body.errors) ? body.errors : [],
    warnings: Array.isArray(body.warnings) ? body.warnings : [],
  };
};

/**
 * A human-usable message from an axios failure, preferring what the control plane said.
 * Its validation failures arrive as { error, errors: [{severity, field, message}] }.
 */
export const ontologyErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const problems = Array.isArray(data?.errors) ? data.errors : [];
  if (problems.length) {
    return problems.map((p) => `${p.field}: ${p.message}`).join('; ');
  }
  return data?.error || data?.message || error?.message || fallback;
};
